import { useState, useEffect, useCallback } from 'react'
import {
  Table, Group, TextInput, Select, Title, Pagination, Badge, ActionIcon,
  Modal, Button, Stack, Text, Card, SimpleGrid,
} from '@mantine/core'
import { IconSearch, IconEdit, IconCurrencyTugrik, IconCheck, IconClock, IconX } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { api } from '@/lib/api'
import { formatName } from '@/lib/format'

const statusOptions = [
  { value: 'all', label: 'Бүгд' },
  { value: 'pending', label: 'Хүлээгдэж буй' },
  { value: 'completed', label: 'Амжилттай' },
  { value: 'failed', label: 'Амжилтгүй' },
]

const typeOptions = [
  { value: 'all', label: 'Бүгд' },
  { value: 'course', label: 'Сургалт' },
  { value: 'premium', label: 'Премиум' },
]

const methodOptions = [
  { value: 'all', label: 'Бүгд' },
  { value: 'qpay', label: 'QPAY' },
  { value: 'bank', label: 'Банк' },
]

const statusEditOptions = [
  { value: 'pending', label: 'Хүлээгдэж буй' },
  { value: 'completed', label: 'Амжилттай' },
  { value: 'failed', label: 'Амжилтгүй' },
]

function statusColor(s: string) {
  if (s === 'completed') return 'green'
  if (s === 'pending') return 'yellow'
  if (s === 'failed') return 'red'
  return 'gray'
}

function statusLabel(s: string) {
  if (s === 'completed') return 'Амжилттай'
  if (s === 'pending') return 'Хүлээгдэж буй'
  if (s === 'failed') return 'Амжилтгүй'
  return s
}

function typeLabel(t: string) {
  if (t === 'course') return 'Сургалт'
  if (t === 'premium') return 'Премиум'
  return t
}

function methodLabel(m: string) {
  if (m === 'qpay') return 'QPAY'
  if (m === 'bank') return 'Банк'
  return m
}

function formatAmount(amount: number) {
  return `${amount.toLocaleString()}₮`
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export default function PaymentList() {
  const [payments, setPayments] = useState<Record<string, unknown>[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1 })
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>('all')
  const [typeFilter, setTypeFilter] = useState<string | null>('all')
  const [methodFilter, setMethodFilter] = useState<string | null>('all')
  const [stats, setStats] = useState({ totalRevenue: 0, completedCount: 0, pendingCount: 0, failedCount: 0 })

  const [editPayment, setEditPayment] = useState<Record<string, unknown> | null>(null)
  const [editStatus, setEditStatus] = useState<string | null>('pending')
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    const params: Record<string, string> = { page: String(pagination.page), limit: '20' }
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter
    if (typeFilter && typeFilter !== 'all') params.type = typeFilter
    if (methodFilter && methodFilter !== 'all') params.method = methodFilter
    if (search) params.search = search
    api.admin.getPayments(params).then((res) => {
      setPayments(res.data)
      setPagination((p) => ({ ...p, pages: res.pagination.pages }))
      setStats(res.stats)
    })
  }, [pagination.page, statusFilter, typeFilter, methodFilter, search])

  useEffect(() => { load() }, [load])

  const openEdit = (payment: Record<string, unknown>) => {
    setEditPayment(payment)
    setEditStatus(payment.status as string)
  }

  const handleSave = async () => {
    if (!editPayment) return
    setSaving(true)
    try {
      await api.admin.updatePayment(editPayment._id as string, { status: editStatus })
      notifications.show({ title: 'Амжилттай', message: 'Төлбөрийн төлөв шинэчлэгдлээ', color: 'green' })
      setEditPayment(null)
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Алдаа гарлаа', color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Title order={2} mb="md">Төлбөрийн жагсаалт</Title>

      {/* Stats */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mb="lg">
        <Card shadow="xs" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <IconCurrencyTugrik size={20} color="var(--mantine-color-blue-6)" />
            <Text size="sm" c="dimmed">Нийт орлого</Text>
          </Group>
          <Text fw={700} size="xl" mt={4}>{formatAmount(stats.totalRevenue)}</Text>
        </Card>
        <Card shadow="xs" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <IconCheck size={20} color="var(--mantine-color-green-6)" />
            <Text size="sm" c="dimmed">Амжилттай</Text>
          </Group>
          <Text fw={700} size="xl" mt={4}>{stats.completedCount}</Text>
        </Card>
        <Card shadow="xs" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <IconClock size={20} color="var(--mantine-color-yellow-6)" />
            <Text size="sm" c="dimmed">Хүлээгдэж буй</Text>
          </Group>
          <Text fw={700} size="xl" mt={4}>{stats.pendingCount}</Text>
        </Card>
        <Card shadow="xs" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <IconX size={20} color="var(--mantine-color-red-6)" />
            <Text size="sm" c="dimmed">Амжилтгүй</Text>
          </Group>
          <Text fw={700} size="xl" mt={4}>{stats.failedCount}</Text>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Хэрэглэгч хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ flex: 1, minWidth: 180 }}
        />
        <Select
          data={statusOptions}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ minWidth: 140 }}
          placeholder="Төлөв"
        />
        <Select
          data={typeOptions}
          value={typeFilter}
          onChange={(v) => { setTypeFilter(v); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ minWidth: 120 }}
          placeholder="Төрөл"
        />
        <Select
          data={methodOptions}
          value={methodFilter}
          onChange={(v) => { setMethodFilter(v); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ minWidth: 140 }}
          placeholder="Төлбөрийн арга"
        />
      </Group>

      {/* Table */}
      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хэрэглэгч</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төрөл</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Сургалт / Хүсэлт</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Дүн</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Арга</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төлөв</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {payments.map((p, i) => {
                const user = p.userId as Record<string, unknown> | null
                const course = p.courseId as Record<string, unknown> | null
                return (
                  <Table.Tr key={p._id as string}>
                    <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                    <Table.Td>
                      <div>
                        <Text size="sm" fw={500}>{formatName(user?.fullName as string) || '-'}</Text>
                        <Text size="xs" c="dimmed">{user?.email as string || ''}</Text>
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={p.type === 'premium' ? 'violet' : 'blue'} variant="light">
                        {typeLabel(p.type as string)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{course?.title as string || (p.type === 'premium' ? 'Премиум эрх' : '-')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>{formatAmount(p.amount as number)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="outline">{methodLabel(p.method as string)}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={statusColor(p.status as string)}>
                        {statusLabel(p.status as string)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{p.createdAt ? formatDate(p.createdAt as string) : '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(p)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
              {payments.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text ta="center" c="dimmed" py="xl">Төлбөр олдсонгүй</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))} />
          </Group>
        )}
      </Card>

      {/* Edit Modal */}
      <Modal opened={!!editPayment} onClose={() => setEditPayment(null)} title="Төлбөрийн төлөв засах" centered>
        <Stack>
          {editPayment && (
            <>
              <Text size="sm">
                <Text span fw={600}>Хэрэглэгч: </Text>
                {formatName((editPayment.userId as Record<string, unknown>)?.fullName as string) || '-'}
              </Text>
              <Text size="sm">
                <Text span fw={600}>Дүн: </Text>
                {formatAmount(editPayment.amount as number)}
              </Text>
              <Select label="Төлөв" data={statusEditOptions} value={editStatus} onChange={setEditStatus} />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setEditPayment(null)}>Болих</Button>
                <Button onClick={handleSave} loading={saving}>Хадгалах</Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </div>
  )
}
